import { Component, Inject, Optional, SkipSelf } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AssetItem, Environment, ENVIRONMENT, GetAssetResponse } from '@assethub/shared/models';
import {
  AssetSelectionService,
  ConfirmationService,
  LanguageService,
  LicensingService,
  TrashService,
  TreeService,
} from '@assethub/shared/services';
import { MessageService } from 'primeng/api';
import { catchError, finalize, tap } from 'rxjs';
import { BasicMenuComponent } from '../menu/basic-menu.component';
import { BasicMenuItemAssetComponent } from './basic-menu-item-asset.component';
import { MenuItemComponent } from '../menu/menu-item/menu-item.component';

@Component({
  selector: 'app-menu-item-asset-delete',
  templateUrl: './basic-menu-item-asset.component.html',
  styleUrls: ['./basic-menu-item-asset.component.scss'],
  imports: [MenuItemComponent],
})
export class MenuItemAssetDeleteComponent extends BasicMenuItemAssetComponent {
  static deleteTree = false;

  constructor(
    sanitizer: DomSanitizer,
    @SkipSelf() @Optional() parentMenu: BasicMenuComponent | null,
    languageService: LanguageService,
    router: Router,
    activatedRoute: ActivatedRoute,
    treeService: TreeService,
    licensingService: LicensingService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private assetSelectionService: AssetSelectionService,
    private trashService: TrashService,
    @Inject(ENVIRONMENT) private settings: Environment,
  ) {
    super(
      sanitizer,
      parentMenu,
      languageService,
      router,
      activatedRoute,
      treeService,
      licensingService,
    );
    MenuItemAssetDeleteComponent.deleteTree = this.settings.envSpecificFeatures.deleteTree;
    this.setup('delete', 'assetDetails.menu.delete');
  }

  static override isViableOption(asset: GetAssetResponse | AssetItem | undefined): boolean {
    if (BasicMenuItemAssetComponent.isManagedExternally(asset)) {
      return false;
    }
    if (BasicMenuItemAssetComponent.isRoot(asset)) {
      if (!this.deleteTree) {
        return false;
      }
      return BasicMenuItemAssetComponent.isAdministrable(asset);
    }
    return BasicMenuItemAssetComponent.isWritable(asset);
  }

  protected override getReason(): string {
    if (BasicMenuItemAssetComponent.isManagedExternally(this.asset)) {
      return 'managedExternally';
    }
    if (BasicMenuItemAssetComponent.isRoot(this.asset)) {
      if (!MenuItemAssetDeleteComponent.deleteTree) {
        return 'deleteTreeNotAllowed';
      }
      if (!BasicMenuItemAssetComponent.isAdministrable(this.asset)) {
        return 'deleteTreeRequiresAdmin';
      }
    }
    if (!BasicMenuItemAssetComponent.isWritable(this.asset)) {
      return 'deleteRequiresWrite';
    }
    return '';
  }

  override onClick() {
    if (!this.asset || this.disabled) {
      return;
    }
    super.onClick();
    const assetUuid = this.asset.uuid;
    this.assetSelectionService.markNode(assetUuid);
    const isRoot = BasicMenuItemAssetComponent.isRoot(this.asset);
    const notificationKey = isRoot ? 'toasts.tree.trashed' : 'toasts.asset.trashed';
    this.confirmationService.confirm({
      translationKey: 'confirm.asset.moveToTrash',
      accept: () =>
        this.trashService.moveToTrash(assetUuid).pipe(
          tap(() => {
            this.forgetAsset(assetUuid);
            this.messageService.add({
              severity: 'info',
              summary: notificationKey,
              life: 10000,
            });
          }),
          catchError(fail => {
            this.messageService.add({
              severity: 'error',
              summary: 'toasts.asset.move-trash-asset-error',
              life: 10000,
            });
            throw fail;
          }),
          finalize(() => this.assetSelectionService.markNode()),
        ),
      reject: () => this.assetSelectionService.markNode(),
    });
  }
}
